<template>
  <div class="ds-page">
    <header class="ds-topbar">
      <div class="ds-topbar__brand">
        <img class="ds-topbar__logo-img" :src="logoUrl" alt="GLT" />
        <div class="ds-topbar__brand-text">
          <div class="ds-topbar__brand-name">GLT</div>
          <div class="ds-topbar__brand-sub">Electronic signature service</div>
        </div>
      </div>
      <div v-if="contract" class="ds-topbar__envelope">
        <span class="ds-topbar__envelope-label">Envelope</span>
        <strong>{{ contract.contractNo }}</strong>
      </div>
    </header>

    <nav class="ds-stepper" v-if="!loading">
      <div
        v-for="(label, i) in stepLabels"
        :key="label"
        class="ds-stepper__node"
        :class="{
          'ds-stepper__node--active': step === i + 1,
          'ds-stepper__node--done': step > i + 1
        }"
      >
        <div class="ds-stepper__bubble">{{ i + 1 }}</div>
        <div class="ds-stepper__label">{{ label }}</div>
      </div>
    </nav>

    <main class="ds-stage" v-loading="loading">
      <template v-if="errorText">
        <div class="ds-card ds-card--error">
          <h2>Unable to open this contract</h2>
          <p>{{ errorText }}</p>
        </div>
      </template>

      <!-- Step 1: Welcome -->
      <section v-if="!errorText && step === 1" class="ds-card ds-card--welcome">
        <div class="ds-welcome">
          <div class="ds-welcome__left">
            <div class="ds-welcome__eyebrow">YOU HAVE A DOCUMENT TO SIGN</div>
            <h1 class="ds-welcome__title">Please review &amp; sign</h1>
            <p class="ds-welcome__sub">
              {{ contract && contract.contractTitle ? contract.contractTitle : 'GLT contract' }}
            </p>
            <div class="ds-chips">
              <span class="ds-chip">{{ contract && contract.contractNo }}</span>
              <span class="ds-chip">{{ formatUsd(contract && contract.quoteTotalAmount) }} {{ (contract && contract.currencyCode) || 'USD' }}</span>
              <span v-if="contract && contract.contractTermMonths != null" class="ds-chip">{{ contract.contractTermMonths }} months</span>
            </div>
            <ul class="ds-welcome__list">
              <li><strong>Start date</strong> {{ (contract && contract.startDate) || '—' }}</li>
              <li><strong>End date</strong> {{ (contract && contract.endDate) || '—' }}</li>
              <li v-if="contract && contract.publicViewExpiresAt"><strong>Link expires</strong> {{ formatTs(contract.publicViewExpiresAt) }}</li>
            </ul>
            <div class="ds-actions ds-actions--centered">
              <button
                class="ds-btn ds-btn--primary"
                :disabled="!canStart"
                @click="goStep(2)"
              >
                {{ alreadySigned ? 'View signed contract' : 'START' }}
              </button>
              <p v-if="!canStart && !alreadySigned" class="ds-hint ds-hint--warn">
                This contract is not currently available for signing.
              </p>
            </div>
          </div>
          <div class="ds-welcome__right">
            <div class="ds-page-thumb">
              <div class="ds-page-thumb__head">CONTRACT</div>
              <div class="ds-page-thumb__lines">
                <span v-for="n in 9" :key="n" class="ds-page-thumb__line"></span>
              </div>
              <div class="ds-page-thumb__sign">
                <span class="ds-page-thumb__sign-tag">SIGN HERE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Step 2: Consent -->
      <section v-if="!errorText && step === 2" class="ds-card">
        <h2 class="ds-card__title">Electronic Records and Signature Disclosure</h2>
        <p class="ds-card__lead">
          To proceed with electronic signing, please review the disclosure below and confirm your consent.
        </p>
        <div class="ds-consent">
          <h4>Please read the Electronic Record and Signature Disclosure</h4>
          <p>
            By selecting the checkbox below, I confirm that I have reviewed the Electronic Record and Signature
            Disclosure, agree to use electronic records and signatures, and consent to do business electronically with
            GLT. I understand I may withdraw consent or request paper copies at any time by contacting GLT.
          </p>
          <ul>
            <li>This document and any related records will be provided to me electronically.</li>
            <li>I may print or save a copy of the executed contract for my records.</li>
            <li>The recorded signature, IP address, and timestamp serve as evidence of my intent to sign.</li>
          </ul>
        </div>
        <label class="ds-consent__checkbox">
          <input type="checkbox" v-model="consentAccepted" />
          <span>I agree to use electronic records and signatures.</span>
        </label>
        <div class="ds-actions">
          <button class="ds-btn ds-btn--ghost" @click="goStep(1)">Back</button>
          <button
            class="ds-btn ds-btn--primary"
            :disabled="!consentAccepted || consentSubmitting"
            @click="submitConsent"
          >
            CONTINUE
          </button>
        </div>
      </section>

      <!-- Step 3: Review (PDF preview) -->
      <section v-if="!errorText && step === 3" class="ds-card ds-card--review">
        <h2 class="ds-card__title">Review the contract</h2>
        <p class="ds-card__lead">Scroll through every page. The Continue button activates once you reach the end.</p>
        <div class="ds-pdf-wrap" ref="pdfScroller" @scroll="onPdfScroll">
          <div v-if="!pdfPages.length && !pdfLoading" class="ds-pdf-empty">
            <p>The contract document is not yet available as PDF for in-line preview.</p>
            <p>
              <button class="ds-btn ds-btn--ghost" @click="downloadContract">Download a copy instead</button>
            </p>
          </div>
          <div v-show="pdfLoading" class="ds-pdf-loading">Loading preview…</div>
          <canvas
            v-for="(p, i) in pdfPages"
            :key="i"
            :ref="'pdfCanvas' + i"
            class="ds-pdf-canvas"
          ></canvas>
        </div>
        <div class="ds-actions ds-actions--split">
          <button class="ds-btn ds-btn--ghost" @click="goStep(2)">Back</button>
          <div>
            <button class="ds-btn ds-btn--link" @click="downloadContract">Download a copy</button>
            <button
              class="ds-btn ds-btn--primary"
              :disabled="!pdfReachedBottom && pdfPages.length > 0"
              @click="goStep(4)"
            >
              {{ pdfReachedBottom || !pdfPages.length ? 'CONTINUE' : 'Scroll to the end…' }}
            </button>
          </div>
        </div>
      </section>

      <!-- Step 4: Sign (Adopt Your Signature) -->
      <section v-if="!errorText && step === 4" class="ds-card">
        <h2 class="ds-card__title">Adopt your signature</h2>
        <p class="ds-card__lead">Confirm your name and choose how you want to sign.</p>

        <div class="ds-form-grid">
          <label class="ds-field">
            <span>Full name *</span>
            <input v-model="signForm.signerName" type="text" placeholder="Your full legal name" />
          </label>
          <label class="ds-field">
            <span>Email</span>
            <input v-model="signForm.signerEmail" type="email" placeholder="you@example.com" />
          </label>
          <label class="ds-field ds-field--full">
            <span>Note (optional)</span>
            <textarea v-model="signForm.note" rows="2" placeholder="Any comments for the counter-party"></textarea>
          </label>
        </div>

        <div class="ds-method-tabs">
          <button
            class="ds-method-tab"
            :class="{ 'ds-method-tab--active': method === 'draw' }"
            @click="setMethod('draw')"
          >Draw</button>
          <button
            class="ds-method-tab"
            :class="{ 'ds-method-tab--active': method === 'type' }"
            @click="setMethod('type')"
          >Type</button>
          <button
            class="ds-method-tab"
            :class="{ 'ds-method-tab--active': method === 'upload' }"
            @click="setMethod('upload')"
          >Upload</button>
        </div>

        <div v-if="method === 'draw'" class="ds-method-pane">
          <div class="ds-sig-pad" ref="sigPadWrap">
            <canvas ref="sigCanvas" class="ds-sig-canvas"></canvas>
            <div class="ds-sig-baseline">Sign here</div>
          </div>
          <div class="ds-pane-actions">
            <button class="ds-btn ds-btn--text" @click="clearDraw">Clear</button>
          </div>
        </div>

        <div v-else-if="method === 'type'" class="ds-method-pane">
          <p class="ds-hint">Type your name and pick a style.</p>
          <input
            v-model="typeName"
            class="ds-type-input"
            placeholder="Type your name here"
          />
          <div class="ds-type-grid">
            <div
              v-for="font in TYPE_FONTS"
              :key="font.family"
              class="ds-type-option"
              :class="{ 'ds-type-option--active': typeFontIndex === font.idx }"
              :style="{ fontFamily: font.css }"
              @click="typeFontIndex = font.idx"
            >
              <span class="ds-type-sample">{{ typeName || 'Your Name' }}</span>
              <span class="ds-type-style">{{ font.label }}</span>
            </div>
          </div>
        </div>

        <div v-else-if="method === 'upload'" class="ds-method-pane">
          <p class="ds-hint">Upload a PNG or JPG of your signature (max 200KB).</p>
          <input ref="uploadInput" type="file" accept="image/png,image/jpeg" @change="onSignatureFile" style="display:none" />
          <button class="ds-btn ds-btn--ghost" @click="$refs.uploadInput && $refs.uploadInput.click()">Choose file…</button>
          <div v-if="uploadPreview" class="ds-upload-preview">
            <img :src="uploadPreview" alt="signature" />
          </div>
        </div>

        <div class="ds-actions ds-actions--split">
          <button class="ds-btn ds-btn--ghost" @click="goStep(3)">Back</button>
          <button class="ds-btn ds-btn--primary" :disabled="!stagedSignatureReady" @click="prepareConfirm">ADOPT &amp; CONTINUE</button>
        </div>
      </section>

      <!-- Step 5: Confirm -->
      <section v-if="!errorText && step === 5" class="ds-card">
        <h2 class="ds-card__title">Confirm and finish</h2>
        <p class="ds-card__lead">Review the details below. By clicking FINISH you are electronically signing this contract.</p>
        <div class="ds-summary">
          <div class="ds-summary__col">
            <div class="ds-summary__row"><span>Contract</span><strong>{{ contract && contract.contractNo }}</strong></div>
            <div class="ds-summary__row"><span>Title</span><strong>{{ (contract && contract.contractTitle) || '—' }}</strong></div>
            <div class="ds-summary__row"><span>Amount</span><strong>{{ formatUsd(contract && contract.quoteTotalAmount) }} {{ (contract && contract.currencyCode) || 'USD' }}</strong></div>
            <div class="ds-summary__row"><span>Signer</span><strong>{{ signForm.signerName || '—' }}</strong></div>
            <div class="ds-summary__row"><span>Email</span><strong>{{ signForm.signerEmail || '—' }}</strong></div>
            <div class="ds-summary__row"><span>Method</span><strong>{{ methodLabel(method) }}</strong></div>
          </div>
          <div class="ds-summary__preview">
            <div class="ds-summary__preview-label">Your signature</div>
            <img v-if="stagedSignaturePng" :src="stagedSignaturePng" alt="signature preview" />
            <div v-else class="ds-summary__preview-empty">No signature</div>
          </div>
        </div>
        <div class="ds-actions ds-actions--split">
          <button class="ds-btn ds-btn--ghost" @click="goStep(4)">Back</button>
          <button class="ds-btn ds-btn--primary ds-btn--lg" :disabled="signing" @click="submitSignature">
            {{ signing ? 'Signing…' : 'FINISH' }}
          </button>
        </div>
      </section>

      <!-- Step 6: Finish -->
      <section v-if="!errorText && step === 6" class="ds-card ds-card--finish">
        <div class="ds-finish__check">
          <svg viewBox="0 0 64 64" width="80" height="80" fill="none" stroke="currentColor" stroke-width="5">
            <circle cx="32" cy="32" r="29" />
            <path d="M18 33 L29 44 L46 23" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <h2 class="ds-finish__title">{{ bothSigned ? 'Fully executed' : "You're all set" }}</h2>
        <p class="ds-finish__sub">
          <template v-if="bothSigned">
            Both parties have signed. The fully-executed contract is shown below; you can download a copy for your records.
          </template>
          <template v-else>
            Your signature has been recorded. GLT will be notified and will counter-sign the contract shortly.
            You can review your signed copy below and download it now; the fully-executed version will be available here
            once GLT signs.
          </template>
        </p>

        <div class="ds-signed-preview" v-if="signedPdfAvailable">
          <div class="ds-signed-preview__head">
            {{ bothSigned ? 'Fully executed contract' : 'Your signed contract' }}
          </div>
          <div class="ds-pdf-wrap ds-pdf-wrap--signed" ref="signedPdfScroller">
            <div v-show="signedPdfLoading" class="ds-pdf-loading">Loading signed contract…</div>
            <canvas
              v-for="(p, i) in signedPdfPages"
              :key="'signed-' + i"
              :ref="'signedPdfCanvas' + i"
              class="ds-pdf-canvas"
            ></canvas>
          </div>
        </div>

        <div class="ds-actions ds-actions--centered">
          <button class="ds-btn ds-btn--primary" @click="downloadContract">
            {{ bothSigned ? 'Download fully-executed copy' : 'Download my signed copy' }}
          </button>
        </div>
      </section>
    </main>

    <footer class="ds-footer">
      <span>Powered by GLT CRM</span>
      <span v-if="contract && contract.publicViewExpiresAt">
        · Link valid until {{ formatTs(contract.publicViewExpiresAt) }}
      </span>
    </footer>
  </div>
</template>

<script>
import axios from 'axios'
import SignaturePad from 'signature_pad'
import { resolveApiBase } from '@/utils/apiBase'
import { formatUsd } from '@/utils/formatUsd'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'
import logo from '@/assets/images/logo.png'

const baseURL = resolveApiBase()
const PDF_WORKER_URL =
  process.env.VUE_APP_PDFJS_WORKER_URL ||
  'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/legacy/build/pdf.worker.min.js'

const TYPE_FONTS = [
  { idx: 0, family: 'Caveat', label: 'Caveat', css: "'Caveat', cursive" },
  { idx: 1, family: 'Dancing Script', label: 'Dancing', css: "'Dancing Script', cursive" },
  { idx: 2, family: 'Allura', label: 'Allura', css: "'Allura', cursive" },
  { idx: 3, family: 'Great Vibes', label: 'Great Vibes', css: "'Great Vibes', cursive" },
  { idx: 4, family: 'Pacifico', label: 'Pacifico', css: "'Pacifico', cursive" },
  { idx: 5, family: 'Sacramento', label: 'Sacramento', css: "'Sacramento', cursive" },
  { idx: 6, family: 'Homemade Apple', label: 'Homemade', css: "'Homemade Apple', cursive" }
]

const TYPE_FONTS_LINK_ID = 'ds-type-fonts-link'
const TYPE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Caveat&family=Dancing+Script&family=Allura&family=Great+Vibes&family=Pacifico&family=Sacramento&family=Homemade+Apple&display=swap'

const STEP_LABELS_EN = ['Welcome', 'Consent', 'Review', 'Sign', 'Confirm', 'Done']
const STEP_LABELS_ZH = ['欢迎', '同意', '查看', '签署', '确认', '完成']

export default {
  name: 'PublicContract',
  data() {
    return {
      TYPE_FONTS,
      logoUrl: logo,
      step: 1,
      loading: false,
      errorText: '',
      contract: null,
      details: [],
      project: null,
      canSign: false,
      alreadySigned: false,
      consentAccepted: false,
      consentSubmitting: false,
      pdfPages: [],
      pdfLoading: false,
      pdfReachedBottom: false,
      pdfDoc: null,
      signedPdfDoc: null,
      signedPdfPages: [],
      signedPdfLoading: false,
      method: 'draw',
      sigPad: null,
      typeName: '',
      typeFontIndex: 0,
      uploadPreview: '',
      uploadDataUrl: '',
      stagedSignaturePng: '',
      signing: false,
      signForm: {
        signerName: '',
        signerEmail: '',
        note: ''
      }
    }
  },
  computed: {
    stepLabels() {
      return this.$locale === 'zh-CN' ? STEP_LABELS_ZH : STEP_LABELS_EN
    },
    canStart() {
      if (this.alreadySigned) return true
      return this.canSign
    },
    stagedSignatureReady() {
      const nameOk = this.signForm.signerName && this.signForm.signerName.trim().length > 0
      if (!nameOk) return false
      if (this.method === 'draw') return this.sigPad && !this.sigPad.isEmpty()
      if (this.method === 'type') return this.typeName && this.typeName.trim().length > 0
      if (this.method === 'upload') return !!this.uploadDataUrl
      return false
    },
    bothSigned() {
      return !!(this.contract && this.contract.companySignedAt)
    },
    signedPdfAvailable() {
      const c = this.contract
      if (!c) return false
      return !!(c.finalPackageFileUrl || c.customerSignedPdfUrl)
    }
  },
  watch: {
    typeName() {
      // 即时刷新 type 预览（无 DOM 操作，纯样式驱动）
    },
    step(val) {
      if (val === 3) {
        this.$nextTick(() => this.maybeLoadPdf())
      }
      if (val === 4) {
        this.$nextTick(() => this.setupDrawPad())
      }
      if (val === 6) {
        this.$nextTick(() => this.maybeLoadSignedPdf())
      }
    }
  },
  mounted() {
    this.injectTypeFonts()
    this.load()
  },
  beforeDestroy() {
    if (this.sigPad) {
      this.sigPad.off()
      this.sigPad = null
    }
  },
  methods: {
    formatUsd,
    formatTs(ts) {
      return formatDisplayDateTime(ts) || ts
    },
    methodLabel(m) {
      if (m === 'draw') return 'Drawn'
      if (m === 'type') return 'Typed'
      if (m === 'upload') return 'Uploaded'
      return '—'
    },
    injectTypeFonts() {
      if (typeof document === 'undefined') return
      if (document.getElementById(TYPE_FONTS_LINK_ID)) return
      const link = document.createElement('link')
      link.id = TYPE_FONTS_LINK_ID
      link.rel = 'stylesheet'
      link.href = TYPE_FONTS_HREF
      document.head.appendChild(link)
    },
    async load() {
      this.loading = true
      this.errorText = ''
      try {
        const token = this.$route.params.token
        const res = await axios.get(`${baseURL}/api/public/contract/${token}`)
        const payload = res.data && res.data.data ? res.data.data : res.data
        this.contract = payload.contract
        this.details = payload.details || []
        this.project = payload.project || null
        this.canSign = !!payload.canSign
        this.alreadySigned = !!(this.contract && this.contract.customerSignedAt)
        if (this.alreadySigned) {
          this.step = 6
          this.resetSignedPdf()
          this.$nextTick(() => this.maybeLoadSignedPdf())
        }
      } catch (e) {
        const code = e && e.response && e.response.status
        if (code === 410) this.errorText = 'This signing link has expired. Please contact GLT to resend.'
        else if (code === 404) this.errorText = 'This signing link is invalid or no longer available.'
        else this.errorText = (e && e.message) || 'Unable to load the contract.'
      } finally {
        this.loading = false
      }
    },
    goStep(n) {
      if (n < 1 || n > 6) return
      if (this.alreadySigned && n !== 6) return
      this.step = n
    },
    async submitConsent() {
      if (!this.consentAccepted) return
      this.consentSubmitting = true
      try {
        const token = this.$route.params.token
        await axios.post(`${baseURL}/api/public/contract/${token}/consent`, {
          consentTextVersion: 'v1'
        })
        this.goStep(3)
      } catch (e) {
        this.$message && this.$message.error
          ? this.$message.error('Unable to record consent. Please try again.')
          : alert('Unable to record consent. Please try again.')
      } finally {
        this.consentSubmitting = false
      }
    },
    async maybeLoadPdf() {
      if (this.pdfDoc || this.pdfLoading) return
      const c = this.contract
      const hasPdf = c && c.generatedFileUrl && /\.pdf$/i.test(c.generatedFileUrl)
      if (!hasPdf) {
        this.pdfPages = []
        return
      }
      this.pdfLoading = true
      try {
        const pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf')
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL
        const token = this.$route.params.token
        const url = `${baseURL}/api/public/contract/${token}/pdf`
        const loadingTask = pdfjsLib.getDocument({ url, withCredentials: false })
        this.pdfDoc = await loadingTask.promise
        const total = this.pdfDoc.numPages
        this.pdfPages = []
        for (let i = 1; i <= total; i++) {
          this.pdfPages.push({ num: i })
        }
        await this.$nextTick()
        for (let i = 1; i <= total; i++) {
          await this.renderPdfPage(i)
        }
        this.$nextTick(() => this.onPdfScroll())
      } catch (e) {
        this.pdfPages = []
      } finally {
        this.pdfLoading = false
      }
    },
    async renderPdfPage(pageNum) {
      if (!this.pdfDoc) return
      const page = await this.pdfDoc.getPage(pageNum)
      const canvasRef = this.$refs['pdfCanvas' + (pageNum - 1)]
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef
      if (!canvas) return
      const dpr = window.devicePixelRatio || 1
      const viewport = page.getViewport({ scale: 1.25 })
      canvas.width = Math.floor(viewport.width * dpr)
      canvas.height = Math.floor(viewport.height * dpr)
      canvas.style.width = viewport.width + 'px'
      canvas.style.height = viewport.height + 'px'
      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      await page.render({ canvasContext: ctx, viewport }).promise
    },
    onPdfScroll() {
      const el = this.$refs.pdfScroller
      if (!el) {
        this.pdfReachedBottom = true
        return
      }
      const threshold = 40
      const scrolled = el.scrollTop + el.clientHeight + threshold >= el.scrollHeight
      if (scrolled) this.pdfReachedBottom = true
    },
    resetSignedPdf() {
      this.signedPdfDoc = null
      this.signedPdfPages = []
      this.signedPdfLoading = false
    },
    async maybeLoadSignedPdf() {
      if (!this.signedPdfAvailable) return
      if (this.signedPdfDoc || this.signedPdfLoading) return
      this.signedPdfLoading = true
      try {
        const pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf')
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL
        const token = this.$route.params.token
        const url = `${baseURL}/api/public/contract/${token}/pdf?stage=signed&_=${Date.now()}`
        const loadingTask = pdfjsLib.getDocument({ url, withCredentials: false })
        this.signedPdfDoc = await loadingTask.promise
        const total = this.signedPdfDoc.numPages
        this.signedPdfPages = []
        for (let i = 1; i <= total; i++) {
          this.signedPdfPages.push({ num: i })
        }
        await this.$nextTick()
        for (let i = 1; i <= total; i++) {
          await this.renderSignedPdfPage(i)
        }
      } catch (e) {
        this.signedPdfPages = []
      } finally {
        this.signedPdfLoading = false
      }
    },
    async renderSignedPdfPage(pageNum) {
      if (!this.signedPdfDoc) return
      const page = await this.signedPdfDoc.getPage(pageNum)
      const canvasRef = this.$refs['signedPdfCanvas' + (pageNum - 1)]
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef
      if (!canvas) return
      const dpr = window.devicePixelRatio || 1
      const viewport = page.getViewport({ scale: 1.25 })
      canvas.width = Math.floor(viewport.width * dpr)
      canvas.height = Math.floor(viewport.height * dpr)
      canvas.style.width = viewport.width + 'px'
      canvas.style.height = viewport.height + 'px'
      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      await page.render({ canvasContext: ctx, viewport }).promise
    },
    setupDrawPad() {
      if (this.method !== 'draw') return
      const canvas = this.$refs.sigCanvas
      if (!canvas) return
      const wrap = this.$refs.sigPadWrap
      const rect = wrap.getBoundingClientRect()
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = rect.width * ratio
      canvas.height = 200 * ratio
      canvas.style.width = rect.width + 'px'
      canvas.style.height = '200px'
      canvas.getContext('2d').scale(ratio, ratio)
      if (this.sigPad) {
        this.sigPad.off()
      }
      this.sigPad = new SignaturePad(canvas, {
        backgroundColor: 'rgba(0,0,0,0)',
        penColor: '#0f172a',
        minWidth: 1.4,
        maxWidth: 2.8
      })
    },
    setMethod(m) {
      this.method = m
      if (m === 'draw') {
        this.$nextTick(() => this.setupDrawPad())
      }
    },
    clearDraw() {
      if (this.sigPad) this.sigPad.clear()
    },
    onSignatureFile(e) {
      const f = e.target.files && e.target.files[0]
      if (e.target) e.target.value = ''
      if (!f) return
      if (f.size > 200 * 1024) {
        this.$message && this.$message.warning
          ? this.$message.warning('Image too large. Please keep it under 200KB.')
          : alert('Image too large. Please keep it under 200KB.')
        return
      }
      const fr = new FileReader()
      fr.onload = () => {
        this.uploadDataUrl = String(fr.result || '')
        this.uploadPreview = this.uploadDataUrl
      }
      fr.readAsDataURL(f)
    },
    renderTypedSignaturePng() {
      const font = TYPE_FONTS[this.typeFontIndex] || TYPE_FONTS[0]
      const canvas = document.createElement('canvas')
      const w = 760
      const h = 220
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, w, h)
      ctx.fillStyle = '#0f172a'
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'center'
      ctx.font = "64px " + font.css
      ctx.fillText(this.typeName || 'Your Name', w / 2, h / 2)
      return canvas.toDataURL('image/png')
    },
    prepareConfirm() {
      if (!this.stagedSignatureReady) return
      if (this.method === 'draw') {
        this.stagedSignaturePng = this.sigPad.toDataURL('image/png')
      } else if (this.method === 'type') {
        this.stagedSignaturePng = this.renderTypedSignaturePng()
      } else if (this.method === 'upload') {
        this.stagedSignaturePng = this.uploadDataUrl
      }
      this.goStep(5)
    },
    async submitSignature() {
      if (this.signing) return
      this.signing = true
      try {
        const token = this.$route.params.token
        const methodCode = this.method === 'draw' ? 1 : this.method === 'type' ? 2 : 3
        await axios.post(`${baseURL}/api/public/contract/${token}/sign`, {
          signerName: this.signForm.signerName,
          signerEmail: this.signForm.signerEmail,
          note: this.signForm.note,
          signatureDataUrl: this.stagedSignaturePng,
          signatureMethod: methodCode
        })
        this.alreadySigned = true
        this.resetSignedPdf()
        this.goStep(6)
        await this.load()
      } catch (e) {
        const msg = (e && e.response && e.response.data && (e.response.data.msg || e.response.data.message))
          || (e && e.message)
          || 'Signing failed. Please try again.'
        this.$message && this.$message.error
          ? this.$message.error(msg)
          : alert(msg)
      } finally {
        this.signing = false
      }
    },
    downloadContract() {
      const token = this.$route.params.token
      window.open(`${baseURL}/api/public/contract/${token}/download`, '_blank')
    }
  }
}
</script>

<style scoped>
.ds-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f6f8fc 0%, #eef1f8 100%);
  color: #1f2937;
  font-family: 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  padding-bottom: 32px;
}

.ds-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 32px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.ds-topbar__brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ds-topbar__logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.08em;
}

.ds-topbar__logo-img {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: cover;
  background: #0a0a0c;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.25);
}

.ds-topbar__brand-name {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.ds-topbar__brand-sub {
  font-size: 12px;
  color: #64748b;
}

.ds-topbar__envelope {
  padding: 6px 14px;
  border-radius: 999px;
  background: #f1f5f9;
  font-size: 12px;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 6px;
}

.ds-topbar__envelope strong {
  color: #0f172a;
  font-size: 13px;
}

.ds-stepper {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 18px;
  margin: 22px auto 8px;
  padding: 0 24px;
  flex-wrap: wrap;
}

.ds-stepper__node {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 13px;
}

.ds-stepper__bubble {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #64748b;
  font-weight: 700;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 200ms ease;
}

.ds-stepper__node--active .ds-stepper__bubble {
  background: #2563eb;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.18);
}

.ds-stepper__node--active .ds-stepper__label {
  color: #0f172a;
  font-weight: 600;
}

.ds-stepper__node--done .ds-stepper__bubble {
  background: #2563eb;
  color: #fff;
}

.ds-stepper__node--done .ds-stepper__label {
  color: #166534;
}

.ds-stage {
  max-width: 980px;
  margin: 18px auto 0;
  padding: 0 20px;
}

.ds-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 28px 30px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.ds-card--error {
  border-color: #fecaca;
  background: #fef2f2;
}

.ds-card--error h2 {
  margin: 0 0 8px;
  color: #b91c1c;
  font-size: 18px;
}

.ds-card__title {
  margin: 0 0 6px;
  font-size: 22px;
  color: #0f172a;
}

.ds-card__lead {
  margin: 0 0 18px;
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
}

.ds-welcome {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 28px;
  align-items: center;
}

@media (max-width: 760px) {
  .ds-welcome {
    grid-template-columns: 1fr;
  }
}

.ds-welcome__eyebrow {
  color: #2563eb;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  margin-bottom: 6px;
}

.ds-welcome__title {
  font-size: 30px;
  color: #0f172a;
  margin: 0 0 8px;
  line-height: 1.18;
}

.ds-welcome__sub {
  color: #475569;
  margin: 0 0 18px;
  font-size: 15px;
}

.ds-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.ds-chip {
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 999px;
  font-weight: 600;
}

.ds-welcome__list {
  list-style: none;
  margin: 0 0 24px;
  padding: 0;
}

.ds-welcome__list li {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #334155;
}

.ds-welcome__list strong {
  color: #64748b;
  font-weight: 500;
  margin-right: 12px;
}

.ds-page-thumb {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  min-height: 280px;
  position: relative;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.06);
}

.ds-page-thumb__head {
  font-size: 11px;
  color: #94a3b8;
  letter-spacing: 0.18em;
  font-weight: 700;
  margin-bottom: 14px;
}

.ds-page-thumb__lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ds-page-thumb__line {
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
}

.ds-page-thumb__line:nth-child(odd) {
  width: 92%;
}

.ds-page-thumb__line:nth-child(even) {
  width: 78%;
}

.ds-page-thumb__sign {
  position: absolute;
  right: 18px;
  bottom: 16px;
  width: 130px;
  height: 50px;
  background: #fef9c3;
  border: 1px dashed #f59e0b;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b45309;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.16em;
}

.ds-page-thumb__sign-tag::before {
  content: '\25B6  ';
  margin-right: 4px;
  color: #f59e0b;
}

.ds-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 14px;
}

.ds-actions--centered {
  justify-content: center;
}

.ds-actions--split {
  justify-content: space-between;
}

.ds-actions--split > div {
  display: flex;
  gap: 10px;
  align-items: center;
}

.ds-btn {
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 11px 26px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background: #fff;
  color: #1f2937;
  transition: all 160ms ease;
  letter-spacing: 0.02em;
}

.ds-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ds-btn--primary {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
}

.ds-btn--primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.ds-btn--ghost {
  background: #fff;
  border-color: #cbd5e1;
  color: #1f2937;
}

.ds-btn--ghost:hover:not(:disabled) {
  border-color: #94a3b8;
}

.ds-btn--text {
  background: transparent;
  color: #2563eb;
  padding: 8px 12px;
}

.ds-btn--link {
  background: transparent;
  color: #2563eb;
  border: none;
  padding: 10px 16px;
  text-decoration: underline;
}

.ds-btn--lg {
  padding: 14px 36px;
  font-size: 15px;
}

.ds-hint {
  font-size: 12px;
  color: #64748b;
  margin: 8px 0 0;
}

.ds-hint--warn {
  color: #b45309;
}

.ds-consent {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px 22px;
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 18px;
  font-size: 13px;
  line-height: 1.7;
  color: #334155;
}

.ds-consent h4 {
  margin: 0 0 10px;
  color: #0f172a;
  font-size: 14px;
}

.ds-consent ul {
  padding-left: 18px;
}

.ds-consent__checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #1f2937;
  margin-bottom: 18px;
  user-select: none;
}

.ds-consent__checkbox input {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
}

.ds-card--review .ds-card__lead {
  margin-bottom: 12px;
}

.ds-pdf-wrap {
  background: #cbd5e1;
  border-radius: 10px;
  max-height: 60vh;
  min-height: 360px;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.ds-pdf-canvas {
  background: #fff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
  border-radius: 4px;
  max-width: 100%;
}

.ds-pdf-loading,
.ds-pdf-empty {
  color: #475569;
  background: #fff;
  border-radius: 8px;
  padding: 32px 24px;
  text-align: center;
}

.ds-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

@media (max-width: 640px) {
  .ds-form-grid {
    grid-template-columns: 1fr;
  }
}

.ds-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #475569;
}

.ds-field--full {
  grid-column: span 2;
}

.ds-field input,
.ds-field textarea,
.ds-type-input {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  color: #0f172a;
  background: #fff;
  transition: border-color 120ms ease;
  font-family: inherit;
}

.ds-field input:focus,
.ds-field textarea:focus,
.ds-type-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.ds-method-tabs {
  display: flex;
  gap: 0;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 18px;
  width: fit-content;
}

.ds-method-tab {
  background: #fff;
  border: none;
  color: #475569;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ds-method-tab:not(:last-child) {
  border-right: 1px solid #e2e8f0;
}

.ds-method-tab--active {
  background: #2563eb;
  color: #fff;
}

.ds-method-pane {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px;
  margin-bottom: 18px;
}

.ds-sig-pad {
  position: relative;
  background: #fffef7;
  border: 1px dashed #f59e0b;
  border-radius: 8px;
  overflow: hidden;
  min-height: 200px;
}

.ds-sig-canvas {
  display: block;
  width: 100%;
  height: 200px;
  touch-action: none;
  cursor: crosshair;
}

.ds-sig-baseline {
  position: absolute;
  left: 18px;
  bottom: 8px;
  color: #b45309;
  font-size: 11px;
  letter-spacing: 0.18em;
  font-weight: 700;
}

.ds-pane-actions {
  margin-top: 10px;
}

.ds-type-input {
  width: 100%;
  margin-bottom: 14px;
}

.ds-type-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

@media (min-width: 720px) {
  .ds-type-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.ds-type-option {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 16px;
  cursor: pointer;
  min-height: 76px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 150ms ease;
}

.ds-type-option:hover {
  border-color: #94a3b8;
}

.ds-type-option--active {
  border-color: #2563eb;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.ds-type-sample {
  font-size: 28px;
  line-height: 1.1;
  color: #0f172a;
}

.ds-type-style {
  font-family: inherit;
  font-size: 11px;
  color: #64748b;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.ds-upload-preview {
  margin-top: 14px;
  padding: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: inline-block;
}

.ds-upload-preview img {
  max-width: 320px;
  max-height: 120px;
  display: block;
}

.ds-summary {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 20px;
  margin-bottom: 18px;
}

@media (max-width: 640px) {
  .ds-summary {
    grid-template-columns: 1fr;
  }
}

.ds-summary__col {
  background: #f8fafc;
  border-radius: 10px;
  padding: 18px;
}

.ds-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
}

.ds-summary__row:last-child {
  border-bottom: none;
}

.ds-summary__row span {
  color: #64748b;
}

.ds-summary__row strong {
  color: #0f172a;
  font-weight: 600;
}

.ds-summary__preview {
  background: #fffef7;
  border: 1px dashed #f59e0b;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ds-summary__preview-label {
  font-size: 11px;
  color: #b45309;
  letter-spacing: 0.18em;
  font-weight: 700;
  margin-bottom: 10px;
}

.ds-summary__preview img {
  max-width: 100%;
  max-height: 120px;
}

.ds-summary__preview-empty {
  color: #94a3b8;
  font-size: 13px;
}

.ds-card--finish {
  text-align: center;
  padding: 56px 40px;
}

.ds-finish__check {
  color: #2563eb;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.ds-finish__title {
  font-size: 28px;
  color: #0f172a;
  margin: 0 0 10px;
}

.ds-finish__sub {
  color: #475569;
  font-size: 15px;
  line-height: 1.65;
  max-width: 620px;
  margin: 0 auto 22px;
}

.ds-signed-preview {
  margin: 0 auto 24px;
  max-width: 760px;
  text-align: left;
}

.ds-signed-preview__head {
  font-size: 11px;
  color: #2563eb;
  letter-spacing: 0.18em;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 10px;
}

.ds-pdf-wrap--signed {
  max-height: 60vh;
  min-height: 320px;
}

.ds-footer {
  max-width: 980px;
  margin: 28px auto 0;
  padding: 0 28px;
  color: #94a3b8;
  font-size: 12px;
  text-align: center;
}
</style>
